"use client"
import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2, ShieldAlert } from "lucide-react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { clearStoredAuth } from "../../../utils/authStorage"
import toast from "react-hot-toast"

export default function SecuritySettings() {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  const handleForceLogoutAll = async () => {
    try {
      setLoggingOut(true)

      const userStr = localStorage.getItem("user")
      const user = userStr ? JSON.parse(userStr) : null
      const schoolId = user?.school && (typeof user.school === "object" ? user.school._id : user.school)

      if (!schoolId) {
        toast.error("School context not found")
        return
      }

      await axiosInstance.post(API_ENDPOINTS.schoolForceLogoutAll(schoolId))

      toast.success("All devices have been logged out.")

      // This action also invalidates the admin's own current session, so
      // log out here immediately instead of waiting for the next request to fail.
      clearStoredAuth()
      window.location.href = "/login"
    } catch (err) {
      console.error("Error logging out all devices:", err)
      toast.error(err.response?.data?.message || "Failed to log out all devices")
    } finally {
      setLoggingOut(false)
      setConfirmOpen(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" />
          Logout From All Devices
        </CardTitle>
        <CardDescription>
          Instantly logs out all staff from every device, including this one. Everyone will
          need to log in again.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          onClick={() => setConfirmOpen(true)}
        >
          Logout All Devices
        </Button>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Logout everyone from this school?</AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately log out all staff from every device.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={loggingOut}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleForceLogoutAll}
                disabled={loggingOut}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {loggingOut ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Logging out...
                  </>
                ) : (
                  "Yes, logout everyone"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}
